import { useEffect, useState } from 'react';

import { errorKey } from '../auth/api';
import { useI18n } from '../i18n/I18nProvider';
import { Sheet } from '../ui/Sheet';
import type { GameApi, VipConfig, VipState } from './api';

/**
 * VIP 说明：我在哪一级、每级发什么、下一级还差多少。
 *
 * **整张等级表从服务端来**（`vip_config`），前端不抄一份。抄了的话，
 * 运营改档位就要发一次前端版本，而在那之前玩家看到的权益和实际发的对不上 ——
 * 那是最难解释的一类客诉（"页面上写着 10 局，我领到 5 局"）。
 *
 * 权益分两种，界面上要分得开：
 *
 *   每日免费局   **要领**，领了就属于你（发进 wallet.free_spins）
 *   充值加送     不用领，是当下的等级权益 —— 等级掉了它就没了
 *   每日提现额度 同上，不是"发下来的东西"
 *
 * 混在一起画的话，玩家会以为提现额度也要点一下才有。
 */
export function VipSheet(
  { open, game, token, onClose, vip, money }:
  {
    open: boolean;
    game: GameApi;
    token: string;
    onClose(): void;
    /** 当前状态，从个人页传下来 —— 已经拉过一次了，不再拉第二遍 */
    vip: VipState | null;
    money(n: number): string;
  },
) {
  const { t } = useI18n();
  const [cfg, setCfg] = useState<VipConfig | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setErr('');
    game.vipConfig(token)
      .then((c) => { if (alive) setCfg(c); })
      .catch((e) => { if (alive) setErr(t(errorKey(e))); });
    return () => { alive = false; };
  }, [open, game, token, t]);

  return (
    <Sheet open={open} title={t('vip.title')} onClose={onClose}>
      <div className="stack">
        {vip && (
          <div className="card stack">
            <div className="spread">
              <span className="vip__badge">VIP {vip.level}</span>
              <span className="small dim">
                {t('profile.deposited', { v: money(vip.depositTotal) })}
              </span>
            </div>
            <span className="small dim">
              {vip.nextAt === null
                ? t('profile.maxLevel')
                : t('profile.toNextLevel', {
                    n: vip.nextLevel!,
                    v: money(vip.nextAt - vip.depositTotal),
                  })}
            </span>
          </div>
        )}

        <p className="small dim">{t('vip.basis')}</p>

        {cfg === null && !err && <p className="small dim">{t('action.working')}</p>}

        {cfg && (
          <>
            {/*
              宽内容**在自己的容器里横向滚**，不让 body 横滚 ——
              五列在 375px 上放不下，而"整页能左右拖"会把固定的顶栏底栏也拖走。
            */}
            <div className="vip__tablewrap">
              <table className="vip__table">
                <thead>
                  <tr>
                    <th>{t('vip.level')}</th>
                    <th>{t('vip.threshold')}</th>
                    <th>{t('profile.perkFreeSpins')}</th>
                    <th>{t('profile.perkDiscount')}</th>
                    <th>{t('vip.withdrawLimit')}</th>
                  </tr>
                </thead>
                <tbody>
                  {cfg.levels.map((lv) => (
                    /* 当前这一级高亮 —— 一张六行的表，不标出"我在哪"等于让人自己数 */
                    <tr key={lv.level}
                        className={vip && lv.level === vip.level ? 'vip__row--me' : ''}>
                      <th scope="row">
                        VIP {lv.level}
                        {vip && lv.level === vip.level && (
                          <span className="vip__you">{t('vip.you')}</span>
                        )}
                      </th>
                      <td className="num">{money(lv.minDeposit)}</td>
                      {/* 0 局的那一档写"—"而不是"0"：0 读起来像"有这项但是零"，
                          而实际情况是这一级根本没有每日奖励 */}
                      <td className="num">{lv.dailyFreeSpins || '—'}</td>
                      <td className="num">
                        {lv.depositDiscount
                          ? `+${(lv.depositDiscount * 100).toFixed(0)}%` : '—'}
                      </td>
                      <td className="num">{money(lv.dailyWithdrawLimit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className="vip__notes">
              {/* 要领 vs 不用领，必须说清楚 */}
              <li className="small dim">{t('vip.noteClaim')}</li>
              <li className="small dim">{t('vip.noteDiscount')}</li>
              <li className="small dim">
                {t('vip.noteMinWithdraw', { v: money(cfg.minWithdraw) })}
              </li>
              {/* 只增不减是个对玩家有利的承诺，值得写出来 */}
              <li className="small dim">{t('vip.noteMonotonic')}</li>
            </ul>
          </>
        )}

        {err && <p className="field__err" role="alert">{err}</p>}
      </div>
    </Sheet>
  );
}
